/**
 * HelpPanel -- Toolbox panel that shows contextual help for the current module.
 *
 * The `?` icon in the header opens the toolbox with this panel focused.
 * Content is dispatched by module id -- each module provides its own help view
 * (or a stub). When no module id matches, a generic fallback is shown.
 *
 * Content is intentionally stubbed for now (issue #33 -- wiring + dispatch only).
 * Modules can replace their stub with richer content as they mature.
 */

import { HelpCircle } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export interface HelpPanelProps {
  /** Active module id derived from the current route (e.g. 'oracle', 'character-creator'). */
  currentModule?: string
}

interface ModuleHelpEntry {
  title: string
  body: React.ReactNode
}

// ============================================================================
// Shared layout
// ============================================================================

function HelpBody({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-ue-border-medium shrink-0">
        <HelpCircle size={14} className="text-ue-accent-primary shrink-0" />
        <span
          className="text-ue-text-primary font-medium"
          style={{ fontSize: '11px' }}
        >
          {title}
        </span>
      </div>
      <div className="flex flex-col gap-3 p-3 flex-1 min-h-0">{children}</div>
    </div>
  )
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-ue-text-secondary leading-relaxed"
      style={{ fontSize: '11px' }}
    >
      {children}
    </p>
  )
}

function ComingSoon() {
  return (
    <p
      className="text-ue-text-muted italic"
      style={{ fontSize: '10px' }}
    >
      Full help content coming soon.
    </p>
  )
}

// ============================================================================
// Per-module help stubs
// ============================================================================

function OracleHelp() {
  return (
    <HelpBody title="Oracle -- Help">
      <Paragraph>
        Oracle is the GDD and document editor. Use the sidebar to browse your
        document vault, and the floating toolbar to format the selected text.
      </Paragraph>
      <Paragraph>
        Tip: Type <code>/</code> in the editor to open the slash-command menu
        for quick block insertion.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function CharacterCreatorHelp() {
  return (
    <HelpBody title="Character Creator -- Help">
      <Paragraph>
        The Character Creator builds playable characters from Synty Modular,
        Mixamo, and other providers. Pick a species, then drill into parts,
        outfits, and skin tones in the right-hand column.
      </Paragraph>
      <Paragraph>
        Tip: The Generate column rebuilds the viewport whenever you change a
        provider selection -- watch for the progress spinner.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function ForgeHelp() {
  return (
    <HelpBody title="Forge -- Help">
      <Paragraph>
        Forge is where you design game data -- characters, items, abilities,
        quests, and skill trees. Changes are stored in the project workspace
        and shared with the UE plugin.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function EchoHelp() {
  return (
    <HelpBody title="Echo -- Help">
      <Paragraph>
        Echo is the audio manager. Browse, preview, and organise sound assets;
        Studio mode lets you compose layered audio events.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function PrismHelp() {
  return (
    <HelpBody title="Prism -- Help">
      <Paragraph>
        Prism manages visual reference boards. Drop in images, sketches, and
        mood boards to build out your game's visual language.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function HoardHelp() {
  return (
    <HelpBody title="Hoard -- Help">
      <Paragraph>
        Hoard is your asset library. Tag, search, and preview packs and
        individual assets across projects.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function WorldKitHelp() {
  return (
    <HelpBody title="Demeter -- Help">
      <Paragraph>
        Demeter (formerly WorldKit) handles PCG ecosystems, roads, water, and
        structures for procedurally generated worlds.
      </Paragraph>
      <ComingSoon />
    </HelpBody>
  )
}

function GenericHelp() {
  return (
    <HelpBody title="GameDevBuddy -- Help">
      <Paragraph>
        Welcome to GameDevBuddy. Pick a module from the left activity bar to
        get started; contextual help for that module will appear here.
      </Paragraph>
      <Paragraph>
        Need more? The bug report tool below this panel is the quickest way to
        reach us with feedback or issues.
      </Paragraph>
    </HelpBody>
  )
}

// ============================================================================
// Dispatch table
// ============================================================================

const HELP_BY_MODULE: Record<string, () => React.ReactElement> = {
  oracle: () => <OracleHelp />,
  'character-creator': () => <CharacterCreatorHelp />,
  forge: () => <ForgeHelp />,
  gdbforge: () => <ForgeHelp />,
  echo: () => <EchoHelp />,
  prism: () => <PrismHelp />,
  hoard: () => <HoardHelp />,
  worldkit: () => <WorldKitHelp />,
  demeter: () => <WorldKitHelp />,
}

/** Exposed for tests so contributors can verify new modules get coverage. */
export function getHelpEntry(moduleId: string | undefined): ModuleHelpEntry {
  const render = moduleId ? HELP_BY_MODULE[moduleId] : undefined
  if (render) {
    return { title: moduleId ?? '', body: render() }
  }
  return { title: 'GameDevBuddy', body: <GenericHelp /> }
}

// ============================================================================
// Panel component
// ============================================================================

export function HelpPanel({ currentModule }: HelpPanelProps) {
  const { body } = getHelpEntry(currentModule)
  return <>{body}</>
}
